import React, { useState, useEffect, useRef } from 'react';
import { Collapse, List, Avatar, Typography, Space } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import * as AntIcons from '@ant-design/icons';

const { Panel } = Collapse;
const { Text, Title } = Typography;

/**
 * ListComponent for displaying various types of lists (bullet, numbered, or icon-based)
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing list items
 * @param {Array} props.data.list_items - Array of [icon, mainLine, subLine, color?] tuples
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const ListComponent = ({
  data,
  title = "List Items",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };

  //////////  Component data sync from backend ?????????????????
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  //////////  Component data sync from backend ?????????????????


  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  // Determine the list type based on the first item
  const getListType = () => {
    if (!data || !data.list_items || data.list_items.length === 0) {
      return 'bullet'; // Default to bullet list
    }
    
    const firstItemPrefix = data.list_items[0][0];
    
    if (typeof firstItemPrefix === 'number') {
      return 'number';
    } else if (typeof firstItemPrefix === 'string') {
      // Check if it's a bullet or an icon name
      return firstItemPrefix === 'bullet' ? 'bullet' : 'icon';
    }
    
    return 'bullet'; // Default fallback
  };

  // Default color mapping for known icons (fallback when no color provided)
  const getDefaultIconColor = (iconName) => {
    if (iconName === 'CheckCircleOutlined') return '#52c41a'; // Green
    if (iconName === 'CloseCircleOutlined') return '#f5222d'; // Red
    if (iconName === 'ExclamationCircleOutlined') return '#faad14'; // Yellow/Orange
    return '#6200EE'; // Default purple
  };
  
  // Function to render the appropriate list item marker
  const renderItemMarker = (prefix, index, color) => {
    if (typeof prefix === 'number') {
      // Numbered list
      return (
        <Avatar 
          style={{ 
            backgroundColor: color || '#6200EE', 
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          size={36} // Increased size for consistency with icon avatars
        >
          {prefix}
        </Avatar>
      );
    } else if (prefix === 'bullet') {
      // Bullet list
      return (
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: color || '#6200EE',
            marginRight: '16px',
            marginLeft: '10px',
            marginTop: '8px' // Added margin-top to align with text
          }}
        />
      );
    } else {
      // Icon list - try to dynamically load the icon from @ant-design/icons
      try {
        // Check if the icon exists in AntIcons
        const IconComponent = AntIcons[prefix];
        
        // Determine icon color: use provided color, or default based on icon type
        const iconColor = color || getDefaultIconColor(prefix);
        
        if (IconComponent) {
          return (
            <Avatar 
              style={{ 
                backgroundColor: '#f0f0f0',
                color: iconColor
              }}
              icon={<IconComponent style={{ fontSize: '20px' }} />} // Increased icon size
              size={36} // Increased avatar size from 28 to 36
            />
          );
        } else {
          // Fallback - try to load from resources folder
          return (
            <Avatar 
              src={`/resources/ANT_icons/${prefix}.png`}
              size={36} // Increased avatar size from 28 to 36
              style={{ backgroundColor: '#f0f0f0' }}
            />
          );
        }
      } catch (error) {
        console.error(`Error loading icon: ${prefix}`, error);
        // Fallback to bullet if icon fails to load
        return (
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: color || '#6200EE',
              marginRight: '16px',
              marginLeft: '10px'
            }}
          />
        );
      }
    }
  };
  
  // Get the display type for the current list
  const listType = getListType();
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px'  }}>
                {data?.list_title || title}
              </div>
            ),
            children: (
              <div>
                {/* Optional Description */}
                {data?.description && (
                  <Text style={{ display: 'block', marginBottom: '16px' }}>
                    {data.description}
                  </Text>
                )}
                
                {/* List Items */}
                {data && data.list_items && data.list_items.length > 0 ? (
                  <List
                    itemLayout="horizontal"
                    dataSource={data.list_items}
                    renderItem={(item, index) => {
                      // Support both [prefix, mainLine, subLine] and [prefix, mainLine, subLine, color]
                      const [prefix, mainLine, subLine, color] = item;
                      
                      return (
                        <List.Item key={index} style={{ padding: '8px 0' }}>
                          <List.Item.Meta
                            avatar={renderItemMarker(prefix, index, color)}
                            title={mainLine}
                            description={subLine}
                          />
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <Text>No list items available.</Text>
                )}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default ListComponent;